import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Clock, Phone, MessageCircle, Hash, Mail, Sparkles } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { serviceApi } from '../../services/api'
import { emotionZone } from '../../utils/emotionZone'
import { Avatar } from '../patterns/Avatar'

interface Ev {
  id: string
  subjectRef: string
  name: string
  avatarUrl: string | null
  deptName: string | null
  position: string | null
  personIndex: number | null
  deptIndex: number | null
  hook: string
  source: string
  whenIso: string
  polarity: 'positive' | 'negative'
}

interface EventsData {
  positive: Ev[]
  negative: Ev[]
}

function evSourceIcon(source: string): LucideIcon {
  switch (source) {
    case 'call':
    case 'phone':
      return Phone
    case 'dm':
      return MessageCircle
    case 'channel':
      return Hash
    case 'mail':
      return Mail
    default:
      return Sparkles
  }
}

function IndexChip({ label, index }: { label: string; index: number }) {
  const z = emotionZone(index)
  const pillBg =
    z.text === 'text-destructive'
      ? 'bg-destructive/15'
      : z.text === 'text-amber-500'
        ? 'bg-amber-500/15'
        : 'bg-emerald-500/15'
  return (
    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium tabular-nums ${z.text} ${pillBg}`}>
      {label} {index}
    </span>
  )
}

function EventCard({ e, onOpen }: { e: Ev; onOpen: (id: string) => void }) {
  const Icon = evSourceIcon(e.source)
  const time = new Date(e.whenIso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  return (
    <button
      onClick={() => onOpen(e.id)}
      className="w-full text-left flex items-start gap-2.5 px-2 py-1.5 rounded-md border border-border bg-card hover:bg-muted transition duration-150"
    >
      <Avatar name={e.name} src={e.avatarUrl} className="h-7 w-7 text-[11px] shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1 min-w-0">
          <span className="text-sm font-medium text-foreground truncate">{e.name}</span>
          <span className="shrink-0 text-xs text-muted-foreground">· {time}</span>
        </div>
        {(e.deptName || e.position) && (
          <p className="text-xs text-muted-foreground truncate">
            {[e.deptName, e.position].filter(Boolean).join(' · ')}
          </p>
        )}
        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
          <Icon size={12} className="shrink-0" />
          {e.hook}
        </p>
        {(e.personIndex !== null || e.deptIndex !== null) && (
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {e.personIndex !== null && <IndexChip label="person" index={e.personIndex} />}
            {e.deptIndex !== null && <IndexChip label="dept" index={e.deptIndex} />}
          </div>
        )}
      </div>
    </button>
  )
}

function EventColumn({
  events,
  loading,
  polarity,
}: {
  events: Ev[]
  loading: boolean
  polarity: 'positive' | 'negative'
}) {
  const navigate = useNavigate()
  const open = (id: string) => navigate(`/workspace/service/incident/${id}`)
  const isPositive = polarity === 'positive'

  return (
    <section className="rounded-lg ring-1 ring-border bg-card p-4">
      <h2
        className={`flex items-center gap-2 text-sm font-medium mb-3 ${
          isPositive ? 'text-emerald-500' : 'text-destructive'
        }`}
      >
        {isPositive ? 'Positive' : 'Negative'}
      </h2>
      <div className="flex flex-col gap-1">
        {loading && <p className="py-2 text-sm text-muted-foreground">Loading…</p>}
        {!loading && events.length === 0 && (
          <p className="py-2 text-sm text-muted-foreground">
            {isPositive ? 'No positive events.' : 'No negative events.'}
          </p>
        )}
        {!loading && events.map((e) => <EventCard key={e.id} e={e} onOpen={open} />)}
      </div>
    </section>
  )
}

export default function EventsFeed({ kind }: { kind: 'employee' | 'client' | 'partner' }) {
  const [data, setData] = useState<EventsData | null>(null)

  useEffect(() => {
    setData(null)
    serviceApi
      .events(`window=2&kind=${kind}`)
      .then((d: EventsData) => setData(d))
      .catch(() => setData({ positive: [], negative: [] }))
  }, [kind])

  const loading = data === null

  return (
    <div>
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
        <Clock size={12} />
        Last 48 hours
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <EventColumn events={data?.positive ?? []} loading={loading} polarity="positive" />
        <EventColumn events={data?.negative ?? []} loading={loading} polarity="negative" />
      </div>
    </div>
  )
}
